import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import WorkflowOverview from './WorkflowOverview';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    teamMemberName: '',
    email: '',
    password: '',
    mobileNumber: '',
    teamType: 'sales',
    role: 'Employee'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('employeeToken') ||
        localStorage.getItem('companyToken');

      console.log('🔑 SuperAdmin fetching data with token:', token?.substring(0, 20) + '...');

      const [leadsRes, employeesRes] = await Promise.all([
        axios.get('/leads', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/employees', {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => {
          console.log('⚠️ Employees API failed:', err.message);
          return { data: [] };
        })
      ]);

      // Handle leads response format
      let allLeads = [];
      if (Array.isArray(leadsRes.data)) {
        allLeads = leadsRes.data;
      } else if (leadsRes.data.leads && Array.isArray(leadsRes.data.leads)) {
        allLeads = leadsRes.data.leads;
      }

      console.log('✅ SuperAdmin - Total leads:', allLeads.length);

      setLeads(allLeads);
      setEmployees(Array.isArray(employeesRes.data) ? employeesRes.data : []);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setLeads([]);
      setEmployees([]);
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/employees', newEmployee, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddEmployee(false);
      setNewEmployee({
        teamMemberName: '',
        email: '',
        password: '',
        mobileNumber: '',
        teamType: 'sales',
        role: 'Employee'
      });
      fetchData();
      alert('Employee added successfully');
    } catch (error) {
      console.error('Error adding employee:', error);
      alert('Error adding employee');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'sales_new': 'bg-blue-100 text-blue-800',
      'sales_working': 'bg-blue-200 text-blue-900',
      'forwarded_to_reservation': 'bg-purple-100 text-purple-800',
      'reservation_working': 'bg-purple-200 text-purple-900',
      'vendor_approval_pending': 'bg-yellow-100 text-yellow-800',
      'vendor_approved': 'bg-green-100 text-green-800',
      'quotation_prepared': 'bg-indigo-100 text-indigo-800',
      'quotation_sent': 'bg-cyan-100 text-cyan-800',
      'client_approved': 'bg-green-200 text-green-900',
      'forwarded_to_accounts': 'bg-orange-100 text-orange-800',
      'payment_pending': 'bg-yellow-200 text-yellow-900',
      'completed': 'bg-green-300 text-green-900',
      'cancelled': 'bg-red-100 text-red-800',
      'on_hold': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="p-8">Loading...</div>;

  const stats = {
    totalLeads: leads.length,
    salesLeads: leads.filter(l => l.currentOwner === 'sales').length,
    reservationLeads: leads.filter(l => l.currentOwner === 'reservation').length,
    accountsLeads: leads.filter(l => l.currentOwner === 'accounts').length,
    completedLeads: leads.filter(l => l.workflowStatus === 'completed').length,
    cancelledLeads: leads.filter(l => l.workflowStatus === 'cancelled').length,
    onHoldLeads: leads.filter(l => l.workflowStatus === 'on_hold').length,
    totalRevenue: leads.reduce((sum, l) => sum + (l.quotations?.[0]?.amount || 0), 0),
    salesTeam: employees.filter(e => e.teamType === 'sales').length,
    reservationTeam: employees.filter(e => e.teamType === 'reservation').length,
    accountsTeam: employees.filter(e => e.teamType === 'accounts').length
  };

  // Get cancelled and on-hold leads for the status tracker
  const statusTrackerLeads = leads.filter(l =>
    l.workflowStatus === 'cancelled' || l.workflowStatus === 'on_hold'
  ).sort((a, b) => new Date(b.statusChangedAt || b.updatedAt) - new Date(a.statusChangedAt || a.updatedAt));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Super Admin Dashboard</h1>
          <button
            onClick={() => setShowAddEmployee(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            + Add Employee
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-2 px-4 whitespace-nowrap ${activeTab === 'overview' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`pb-2 px-4 whitespace-nowrap ${activeTab === 'leads' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            All Leads
          </button>
          <button
            onClick={() => setActiveTab('status-tracker')}
            className={`pb-2 px-4 whitespace-nowrap flex items-center gap-2 ${activeTab === 'status-tracker' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-600'}`}
          >
            <span>🚨 Status Tracker</span>
            {(stats.cancelledLeads + stats.onHoldLeads) > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {stats.cancelledLeads + stats.onHoldLeads}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`pb-2 px-4 whitespace-nowrap ${activeTab === 'employees' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            Employees
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={`pb-2 px-4 whitespace-nowrap ${activeTab === 'workflow' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            Lead Workflow
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">Total Leads</h3>
                <p className="text-2xl font-bold">{stats.totalLeads}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">With Sales</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.salesLeads}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">With Reservation</h3>
                <p className="text-2xl font-bold text-purple-600">{stats.reservationLeads}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">With Accounts</h3>
                <p className="text-2xl font-bold text-orange-600">{stats.accountsLeads}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">Completed</h3>
                <p className="text-2xl font-bold text-green-600">{stats.completedLeads}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">Total Revenue</h3>
                <p className="text-2xl font-bold">₹{stats.totalRevenue.toLocaleString()}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">Sales Team</h3>
                <p className="text-2xl font-bold">{stats.salesTeam}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-gray-500 text-sm">Reservation Team</h3>
                <p className="text-2xl font-bold">{stats.reservationTeam}</p>
              </div>
              {/* Cancelled Leads */}
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                <h3 className="text-gray-500 text-sm">❌ Cancelled</h3>
                <p className="text-2xl font-bold text-red-600">{stats.cancelledLeads}</p>
              </div>
              {/* On Hold Leads */}
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                <h3 className="text-gray-500 text-sm">⏸️ On Hold</h3>
                <p className="text-2xl font-bold text-yellow-600">{stats.onHoldLeads}</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Recent Leads Activity</h2>
              <div className="space-y-3">
                {leads.slice(0, 10).map(lead => (
                  <div key={lead._id} className="flex justify-between items-center border-b pb-2">
                    <div>
                      <div className="font-medium">{lead.customerName}</div>
                      <div className="text-sm text-gray-500">{lead.destination}</div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(lead.workflowStatus)}`}>
                        {lead.workflowStatus?.replace(/_/g, ' ')}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        {lead.currentOwner} team
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.map(lead => (
                  <tr key={lead._id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium">{lead.customerName}</div>
                        <div className="text-sm text-gray-500">{lead.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{lead.destination}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(lead.workflowStatus)}`}>
                        {lead.workflowStatus?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100">
                        {lead.currentOwner}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div>Sent: {lead.quotations?.filter(q => q.status === 'sent').length || 0}</div>
                        <div className="text-gray-500">Total: {lead.quotations?.length || 0}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.map(emp => (
                  <tr key={emp._id}>
                    <td className="px-6 py-4 font-medium">{emp.teamMemberName}</td>
                    <td className="px-6 py-4">{emp.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${emp.teamType === 'sales' ? 'bg-blue-100 text-blue-800' :
                        emp.teamType === 'reservation' ? 'bg-purple-100 text-purple-800' :
                          emp.teamType === 'accounts' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {emp.teamType || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{emp.role}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${emp.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {emp.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Status Tracker Tab - Cancelled & On-Hold Leads */}
        {activeTab === 'status-tracker' && (
          <div className="space-y-3">
            {/* Compact Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs opacity-80">Cancelled</p>
                    <p className="text-2xl font-bold">{stats.cancelledLeads}</p>
                  </div>
                  <span className="text-2xl opacity-40">❌</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-3 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs opacity-80">On Hold</p>
                    <p className="text-2xl font-bold">{stats.onHoldLeads}</p>
                  </div>
                  <span className="text-2xl opacity-40">⏸️</span>
                </div>
              </div>
            </div>

            {/* Status Tracker List - Compact */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-700 text-white flex items-center justify-between">
                <h3 className="text-sm font-semibold">📋 Cancelled & On-Hold Leads</h3>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  {statusTrackerLeads.length} total
                </span>
              </div>

              {statusTrackerLeads.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-3xl mb-2">✅</div>
                  <p className="text-gray-600 font-medium">No Cancelled or On-Hold Leads</p>
                  <p className="text-gray-400 text-xs">All leads are active!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {statusTrackerLeads.map(lead => (
                    <div
                      key={lead._id}
                      className={`p-3 hover:bg-gray-50 transition-colors`}
                      style={{ borderLeftWidth: '3px', borderLeftColor: lead.workflowStatus === 'cancelled' ? '#ef4444' : '#eab308' }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Left: Lead Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${lead.workflowStatus === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                              }`}>
                              {lead.workflowStatus === 'cancelled' ? '✕ CANCELLED' : '⏸ ON HOLD'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {lead.leadType === 'other_services'
                                ? lead.serviceType?.replace(/_/g, ' ').toUpperCase() || 'OTHER'
                                : 'TOUR'}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-800 text-sm truncate">{lead.customerName}</h4>
                          <p className="text-xs text-gray-500 truncate">
                            {lead.email || '-'} • {lead.contactNumber || '-'}
                          </p>
                        </div>

                        {/* Right: Reason */}
                        <div className="w-56 flex-shrink-0">
                          <p className="text-xs text-gray-400 mb-0.5">
                            {lead.workflowStatus === 'cancelled' ? 'Reason:' : 'Hold Reason:'}
                          </p>
                          <p className="text-xs text-gray-700 font-medium line-clamp-2">
                            {lead.statusChangeReason || 'No reason'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {lead.statusChangedBy || '-'} • {formatDate(lead.statusChangedAt || lead.updatedAt)}
                          </p>
                        </div>

                        {/* View Button */}
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 flex-shrink-0"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workflow Tab */}
        {activeTab === 'workflow' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Complete Lead Workflow</h3>
            <WorkflowOverview />
          </div>
        )}

        {/* Add Employee Modal */}
        {showAddEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4">Add New Employee</h2>
              <form onSubmit={handleAddEmployee}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.teamMemberName}
                      onChange={(e) => setNewEmployee({ ...newEmployee, teamMemberName: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Password *</label>
                    <input
                      type="password"
                      required
                      value={newEmployee.password}
                      onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Mobile Number</label>
                    <input
                      type="text"
                      value={newEmployee.mobileNumber}
                      onChange={(e) => setNewEmployee({ ...newEmployee, mobileNumber: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Team Type *</label>
                    <select
                      value={newEmployee.teamType}
                      onChange={(e) => setNewEmployee({ ...newEmployee, teamType: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="sales">Sales</option>
                      <option value="reservation">Reservation</option>
                      <option value="accounts">Accounts</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Role *</label>
                    <select
                      value={newEmployee.role}
                      onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="Employee">Employee</option>
                      <option value="Team Lead">Team Lead</option>
                      <option value="Manager">Manager</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddEmployee(false)}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add Employee
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Lead Details Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Lead Details</h2>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="font-semibold mb-2">Customer Information</h3>
                  <p><strong>Name:</strong> {selectedLead.customerName}</p>
                  <p><strong>Email:</strong> {selectedLead.email}</p>
                  <p><strong>Phone:</strong> {selectedLead.contactNumber}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Travel Details</h3>
                  <p><strong>Destination:</strong> {selectedLead.destination}</p>
                  <p><strong>Days:</strong> {selectedLead.numberOfDays}</p>
                  <p><strong>Budget:</strong> {selectedLead.budget}</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-2">Workflow History</h3>
                <div className="space-y-2">
                  {selectedLead.workflowHistory?.map((history, idx) => (
                    <div key={idx} className="border-l-2 border-blue-500 pl-4 py-2">
                      <p className="font-medium">{history.action}</p>
                      <p className="text-sm text-gray-600">
                        {history.fromStatus} → {history.toStatus}
                      </p>
                      <p className="text-xs text-gray-500">
                        By {history.performedByName} on {new Date(history.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Quotations ({selectedLead.quotations?.length || 0})</h3>
                <div className="space-y-2">
                  {selectedLead.quotations?.map((quot, idx) => (
                    <div key={idx} className="border rounded p-3">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">Quotation #{quot.quotationNumber}</p>
                          <p className="text-sm">Amount: ₹{quot.amount?.toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full h-fit ${quot.status === 'approved' ? 'bg-green-100 text-green-800' :
                          quot.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                          {quot.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Prepared by {quot.preparedByName}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
